/*
GPS DataViz

Copyright © 2021 GPSDataViz

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/
import React, { useEffect, useState } from "react";
import {
	Progress,
  Segmented,
  Tooltip
} from "antd";
import { AGGREGATE_OPTIONS } from "../Comparison1V1";

export const AggregateRow = ({ data, handleMetricAnnotationChange })  => {

  const [selectedAggregateOption, setSelectedAggregateOption] = useState(AGGREGATE_OPTIONS.AVG);
  const [metricIdentifier, setMetricIdentifier] = useState(null);
  const [dataToDisplay, setDataToDisplay] = useState(null);

  useEffect(() => {
        const leftValueRaw = data?.leftMetric?.values?.[selectedAggregateOption];
        const rightValueRaw = data?.rightMetric?.values?.[selectedAggregateOption];
        const decimals = data?.leftMetric?.metric?.num_decimals || data?.leftMetric?.metric?.decimal_places || 2;
        const lowGood = data?.leftMetric?.metric?.low_good || false;
        const { left, right } = getLeftAndRightPercentageValues(leftValueRaw, rightValueRaw, decimals, lowGood);
        const aggregatedDataToDisplay = {
            left,
            right,
            leftValueRaw,
            rightValueRaw,
            lowGood,
            colorLeft: data?.colorLeft,
            colorRight: data?.colorRight,
            name: data.name,
            identifier: data.identifier,
            decimal_places: decimals
        }
        setDataToDisplay(aggregatedDataToDisplay);
        setMetricIdentifier(data.identifier);
  }, [data, selectedAggregateOption])

  const handleAggregateOptionChange = (event) => {
    handleMetricAnnotationChange(metricIdentifier, event);
    setSelectedAggregateOption(event);
  }

  const getLeftAndRightPercentageValues = (leftValue = 0, rightValue = 0, decimals = 2, lowGood = false) => {

    if(lowGood) {
      if(leftValue < 0 && rightValue < 0) {
        return {
          left: Number(100 * rightValue / Math.min(leftValue, rightValue).toFixed(decimals)),
          right: Number(100 * leftValue / Math.min(leftValue, rightValue).toFixed(decimals)),
        }
      } else {
        return {
          left: Number(100 * rightValue / Math.max(leftValue, rightValue).toFixed(decimals)),
          right: Number(100 * leftValue / Math.max(leftValue, rightValue).toFixed(decimals)),
        }
      }
    }

    // lowGood == false
    if(leftValue < 0 && rightValue < 0) {
      return {
        left: Number(100 * leftValue / Math.min(leftValue, rightValue).toFixed(decimals)),
        right: Number(100 * rightValue / Math.min(leftValue, rightValue).toFixed(decimals)),
      }
    } else {
      return {
        left: Number(100 * leftValue / Math.max(leftValue, rightValue).toFixed(decimals)),
        right: Number(100 * rightValue / Math.max(leftValue, rightValue).toFixed(decimals)),
      }
    }
  }

  return (
    <>
        <div className="aggregate-row-container-left">
            <Progress 
              percent={dataToDisplay?.left || 0} 
              className="progress-left-container"
              format={percent => `${dataToDisplay?.leftValueRaw?.toFixed(dataToDisplay?.decimal_places) || 0} (${percent.toFixed(0)}%)`}
              strokeColor={dataToDisplay?.colorLeft}
            />
        </div>
        <div className="aggregate-row-container-center">
            <Tooltip title={dataToDisplay?.name || ''}>
              <span className="segmented-label">{dataToDisplay?.name || ''}</span>
            </Tooltip>
            <Segmented options={Object.values(AGGREGATE_OPTIONS)} onChange={handleAggregateOptionChange} />
        </div>
        <div className="aggregate-row-container-right">
            <Progress 
              percent={dataToDisplay?.right || 0} 
              format={percent => `${dataToDisplay?.rightValueRaw?.toFixed(dataToDisplay?.decimal_places) || 0} (${percent.toFixed(0)}%)`}
              strokeColor={dataToDisplay?.colorRight}
            />
        </div>
    </>
  );
}